import { supabase } from './supabase';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ticketsApi } from '../api/ticketsApi';
import { API_BASE_URL, SUPABASE_ONLY } from '../api/client';

import { TicketService } from '../api/supabase/ticketSupabaseApi';
import { type Module, type Ticket } from './mock';
import { useToast } from './toast';
import { useNotifications } from './notifications';

function mentionsIn(body: string): string[] {
  const matches = body.match(/@([a-zA-Z0-9._-]+)/g) || [];
  return matches.map((m) => m.substring(1));
}

type Row = {
  id: string;
  ref: string;
  number?: string;
  module: string;
  subject: string;
  state: string;
  status?: string;
  priority: string;
  requester: string | null;
  requester_id?: string | null;
  requester_email?: string | null;
  assignee: string | null;
  assignee_id?: string | null;
  team: string | null;
  team_id?: string | null;
  category: string | null;
  ci: string | null;
  description: string | null;
  watching: boolean | null;
  sla_due_at: string | null;
  created_at: string;
  merged_into?: string | null;
  impact?: string | null;
  urgency?: string | null;
  due_at?: string | null;
  watchers?: string[] | null;
  custom?: Record<string, unknown> | null;
  resolution_code?: string | null;
  closure_notes?: string | null;
  parent_id?: string | null;
  response_due_at?: string | null;
  escalation_level?: number | null;
  approvals?: Ticket['approvals'];
  ticket_events?: { at: string; kind: string; label: string; actor: string | null }[];
  ticket_comments?: { created_at: string; content: string; is_internal: boolean; author: { full_name: string } | null }[];
  teams?: { name: string } | null;
  categories?: { name: string } | null;
  created_by?: string | null;
  organization_id?: string | null;
  organizations?: { id: string; organization_name: string } | null;
  ticket_attachments?: { id: string; name: string; size: number; content_type: string; path: string }[];
  source?: string | null;
};

function mapPriority(p: string | null | undefined): Ticket['priority'] {
  if (!p) return 'P3';
  if (p === 'P1' || p === 'P2' || p === 'P3' || p === 'P4') return p as Ticket['priority'];
  const s = p.toLowerCase().trim();
  if (s === 'urgent' || s === 'critical') return 'P1';
  if (s === 'high') return 'P2';
  if (s === 'medium') return 'P3';
  if (s === 'low') return 'P4';
  return 'P3';
}

function safeParseDate(val: any, fallback = Date.now()): number {
  if (!val) return fallback;
  const parsed = typeof val === 'number' ? val : Date.parse(val);
  return isNaN(parsed) ? fallback : parsed;
}

export function mapRow(r: Row): Ticket {
  const now = Date.now();
  const createdAt = safeParseDate(r.created_at, now);
  const slaDueAt = safeParseDate(r.sla_due_at, createdAt + 4 * 3600 * 1000);

  return {
    id: r.id,
    ref: r.number || '—',
    module: (r.module as Module) || 'Incident',
    subject: r.subject || '—',
    state: r.status || 'Open',
    priority: mapPriority(r.priority),
    requester: (r as any).requester?.full_name || (r.requester_email ? r.requester_email.split('@')[0] : null) || r.requester_id || '—',
    requesterId: r.requester_id || null,
    requesterEmail: r.requester_email ?? (r as any).requester?.email ?? null,
    assignee: !r.assignee_id || r.assignee_id === '00000000-0000-0000-0000-000000000000' ? null : ((r as any).assignee?.full_name || r.assignee_id),
    assigneeId: !r.assignee_id || r.assignee_id === '00000000-0000-0000-0000-000000000000' ? null : r.assignee_id,
    team: (r as any).team_name || r.teams?.name || '—',
    teamId: !r.team_id || r.team_id === '00000000-0000-0000-0000-000000000000' ? null : r.team_id,
    category: (r as any).category_name || r.categories?.name || '—',
    ci: r.ci,
    description: r.description ?? '',
    watching: Boolean(r.watching),
    createdAt,
    slaDueAt,
    mergedInto: r.merged_into ?? null,
    links: [],
    impact: r.impact ?? undefined,
    urgency: r.urgency ?? undefined,
    dueAt: r.due_at ? safeParseDate(r.due_at, createdAt) : null,
    watchers: r.watchers ?? [],
    custom: r.custom ?? {},
    resolutionCode: r.resolution_code ?? undefined,
    closureNotes: r.closure_notes ?? undefined,
    parentId: r.parent_id ?? null,
    responseDueAt: r.response_due_at ? safeParseDate(r.response_due_at, createdAt) : null,
    escalationLevel: r.escalation_level ?? 0,
    approvals: r.approvals ?? [],
    events: [
      ...(r.ticket_events ?? []).map((e) => ({
        at: safeParseDate(e.at, createdAt),
        kind: e.kind as Ticket['events'][number]['kind'],
        label: e.label,
        actor: e.actor ?? undefined,
        internal: (e as any).internal ?? false,
      })),
      ...(r.ticket_comments ?? []).map((c: any) => ({
        at: safeParseDate(c.created_at, createdAt),
        kind: 'comment' as const,
        label: c.content,
        actor: c.author?.full_name ?? c.author_name ?? undefined,
        internal: c.is_internal,
        source: c.source || 'portal',
      }))
    ].sort((a, b) => a.at - b.at),
    createdBy: r.created_by ?? null,
    organizationId: r.organization_id ?? undefined,
    organizationName: r.organizations?.organization_name ?? undefined,
    source: r.source || 'Web',
    sourceType: (r as any).source_type || (r.source === 'Sentinel' ? 'SENTINEL' : 'USER_CREATED'),
    attachments: (r.ticket_attachments ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      size: a.size,
      type: a.content_type,
      url: supabase ? supabase.storage.from('attachments').getPublicUrl(a.path).data.publicUrl : '',
    })),
  };
}


async function fetchTickets(): Promise<Ticket[] | null> {
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;

  let lastError: any = null;

  // 1. Try Backend API first via centralized endpoint
  if (!SUPABASE_ONLY) try {
    let res = await fetch(`${API_BASE_URL}/tickets`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (res.status === 401 && supabase) {
      // Access token expired, attempt refresh once
      try {
        const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
        if (!refreshErr && refreshData?.session?.access_token) {
          res = await fetch(`${API_BASE_URL}/tickets`, {
            headers: {
              'Authorization': `Bearer ${refreshData.session.access_token}`
            }
          });
        }
      } catch (e) {}
    }

    if (res.ok) {
      const payload = await res.json();
      const rawList = Array.isArray(payload) ? payload : payload.tickets;
      if (Array.isArray(rawList)) {
        return rawList.map(mapRow);
      }
    } else {
      const errJson = await res.json().catch(() => ({}));
      lastError = new Error(errJson.error?.message || errJson.error || `API returned HTTP ${res.status}`);
      console.warn('API /tickets call returned error, attempting direct Supabase select fallback:', lastError.message);
    }
  } catch (apiErr: any) {
    lastError = apiErr;
    console.warn('API /tickets call failed, attempting direct Supabase select fallback:', apiErr.message || apiErr);
  }

  // 2. Direct Supabase select fallback
  try {
    const { data, error } = await supabase
      .from('tickets')
      .select('*, ticket_events(*), ticket_comments(*, author:profiles!ticket_comments_author_id_fkey(full_name)), teams(name), requester:profiles!tickets_requester_id_fkey(full_name), assignee:profiles!tickets_assignee_id_fkey(full_name), organizations(id, organization_name), ticket_attachments(*)')
      .order('created_at', { ascending: false });
    if (!error && data) {
      return (data as Row[]).map(mapRow);
    }
    if (error) lastError = error;
  } catch (err: any) {
    lastError = err;
  }

  // 3. Resilient fallback without joined profiles (safe under user-only RLS)
  try {
    const { data: fbData, error: fbError } = await supabase
      .from('tickets')
      .select('*, ticket_events(*), ticket_comments(*), teams(name), organizations(id, organization_name), ticket_attachments(*)')
      .order('created_at', { ascending: false });
    if (!fbError && fbData) {
      return (fbData as Row[]).map(mapRow);
    }
    if (fbError) lastError = fbError;
  } catch (err: any) {
    lastError = err;
  }

  if (lastError) throw lastError;
  return [];
}

export type DataSource = 'mock' | 'live';

export type NewTicket = {
  module: Module;
  subject: string;
  priority: string;
  description?: string;
  requester?: string;
  requesterId?: string;
  requesterEmail?: string;
  team?: string;
  teamId?: string;
  category?: string;
  ci?: string;
  impact?: string;
  urgency?: string;
  dueAt?: number | null;
  watchers?: string[];
  custom?: Record<string, unknown>;
  slaDueAt?: number;
  responseDueAt?: number;
  source?: string;
  attachments?: File[];
  requestType?: string;
  asset_id?: string;
  cc?: string[];
  bcc?: string[];
};

export type ConsoleStore = {
  tickets: Ticket[];
  source: DataSource;
  isLoading: boolean;
  error: string | null;
  createTicket: (d: NewTicket) => Promise<{ success: boolean; ticket?: Ticket; notification?: any; error?: string }>;
  setStatus: (id: string, state: string) => void;
  assign: (id: string, assignee: string | null, assigneeName?: string) => Promise<{ success: boolean; ticket?: Ticket; error?: string }>;
  addUpdate: (id: string, body: string, internal?: boolean, emailData?: any) => void;
  relate: (id: string, targetId: string) => void;
  merge: (id: string, intoId: string) => void;
  updateField: (id: string, field: string, value: any) => void;
  bulkAssign: (ids: string[], assignee: string | null, assigneeName?: string) => Promise<void>;
  bulkStatus: (ids: string[], state: string) => void;
  bulkUpdate: (ids: string[], body: string) => void;
  addAttachment: (ticketId: string, file: File) => void;
  removeAttachment: (ticketId: string, attId: string) => void;
  resolveTicket: (id: string, state: string, code: string, notes: string) => void;
  setParent: (id: string, parentId: string | null) => void;
  escalate: (id: string, assignee: string, level: number, label: string) => void;
  requestApproval: (id: string, approver: string) => void;
  decideApproval: (id: string, approvalId: string, decision: 'approved' | 'rejected', note?: string) => void;
  refreshTickets: () => Promise<void>;
};

/**
 * The console store. Local-first so the app is fully usable in demo mode;
 * when a real Supabase session exists, writes also persist (and Realtime
 * reconciles the canonical list across clients).
 */
export function useConsole(actor: string): ConsoleStore {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [source, setSource] = useState<DataSource>('live');
  const [orgId, setOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { push: toast } = useToast();
  const { push: notify } = useNotifications();
  const ticketsRef = useRef<Ticket[]>([]);
  ticketsRef.current = tickets;
  const orgRef = useRef<string | null>(null);
  orgRef.current = orgId;
  const userIdRef = useRef<string | null>(null);
  const dbTeamsRef = useRef<{ id: string; name: string }[]>([]);

  // Restore a ticket to a prior snapshot (local + best-effort DB) — used by Undo.
  const restore = useCallback((snap: Ticket) => {
    setTickets((prev) => prev.map((t) => (t.id === snap.id ? snap : t)));
    if (orgRef.current && supabase) {
      supabase
        .from('tickets')
        .update({
          status: snap.state,
          assignee_id: snap.assignee,
          merged_into: snap.mergedInto ?? null,
          resolution_code: snap.resolutionCode ?? null,
          closure_notes: snap.closureNotes ?? null,
          parent_id: snap.parentId ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', snap.id)
        .then(() => {});
    }
  }, []);

  const loadTickets = useCallback(async () => {
    const sb = supabase;
    if (!sb) return;

    const { data: { session } } = await sb.auth.getSession();
    
    if (!session) {
      setOrgId(null);
      setTickets([]);
      setSource('live');
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    setIsLoading(true);
    let rows = null;
    let failureError: any = null;
    try {
      rows = await fetchTickets();
      const { data: teamsData } = await sb.from('teams').select('id, name');
      if (teamsData) dbTeamsRef.current = teamsData;
    } catch (err: any) {
      console.error('fetchTickets or teams failed:', err);
      failureError = err;
    }
    
    let extractedOrgId = null;
    try {
      const { data: profile } = await sb.from('profiles').select('organization_id').eq('id', session.user.id).single();
      if (profile) extractedOrgId = profile.organization_id;
    } catch (e) { console.error('Failed to fetch org_id', e); }
    setOrgId(extractedOrgId);
    userIdRef.current = session.user.id;

    if (rows !== null) {
      setTickets(rows);
      setLoadError(null);
      setSource('live');
    } else {
      setTickets([]);
      setLoadError(failureError?.message || 'Unable to load dashboard data. Database or API connection failed.');
      setSource('live');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const sb = supabase;
    if (!sb) return;
    let cancelled = false;

    const runLoad = async () => {
      if (!cancelled) await loadTickets();
    };

    runLoad();

    // Realtime via Supabase
    const channel = sb.channel('public:tickets')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tickets' },
        (_payload: any) => {
          if (!cancelled) runLoad();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ticket_comments' },
        (_payload: any) => {
          if (!cancelled) runLoad();
        }
      )
      .subscribe();

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'compass_tickets_sync' && !cancelled) runLoad();
    };

    window.addEventListener('compass_auth_changed', runLoad);
    window.addEventListener('compass_tickets_changed', runLoad);
    window.addEventListener('storage', handleStorage);

    return () => {
      cancelled = true;
      channel.unsubscribe();
      window.removeEventListener('compass_auth_changed', runLoad);
      window.removeEventListener('compass_tickets_changed', runLoad);
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadTickets]);

  const notifyTicketEvent = useCallback(async (_ticket: Ticket, _type: 'created' | 'status_changed') => {
    // Intentionally left blank. 
    // Email notifications are now securely handled by the backend database trigger (trg_ticket_email_trigger).
  }, []);

  const patch = useCallback((id: string, fn: (t: Ticket) => Ticket) => {
    setTickets((prev) => prev.map((t) => (t.id === id ? fn(t) : t)));
  }, []);

  const createTicket = useCallback<ConsoleStore['createTicket']>(
    async (d) => {
      try {
        const sessionRes = await supabase?.auth.getSession();
        const token = sessionRes?.data?.session?.access_token;
        if (!token) {
          const errMsg = 'Failed to create ticket: Not authenticated';
          toast(errMsg);
          return { success: false, error: errMsg };
        }

        // UUID validator to sanitize optional foreign keys
        const isValidUuid = (val: any) =>
          typeof val === 'string' &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(val);

        const categoryId = isValidUuid((d as any).categoryId)
          ? (d as any).categoryId
          : (isValidUuid(d.category) ? d.category : null);
        const teamId = isValidUuid(d.teamId) ? d.teamId : null;
        const assigneeId = isValidUuid((d as any).assigneeId) ? (d as any).assigneeId : null;
        const requesterId = isValidUuid(d.requesterId)
          ? d.requesterId
          : (isValidUuid(userIdRef.current) ? userIdRef.current : null);

        let res: Response | null = null;
        if (!SUPABASE_ONLY) res = await fetch(`${API_BASE_URL}/tickets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subject: d.subject.trim(),
            description: d.description ? d.description.trim() : '',
            priority: d.priority,
            impact: d.impact || undefined,
            urgency: d.urgency || undefined,
            category: d.category || 'General',
            category_id: categoryId,
            team_id: teamId,
            assignee_id: assigneeId,
            requester_id: requesterId,
            requester_name: d.requester,
            requester_email: d.requesterEmail,
            module: d.module || 'Incident',
            requestType: d.requestType || d.module,
            asset_id: d.asset_id || d.ci,
            cc: d.cc || [],
            bcc: d.bcc || [],
            custom: {
              ...(d.custom || {}),
              cc: d.cc || [],
              bcc: d.bcc || [],
              ci: d.ci || undefined
            }
          })
        });

        let createdRow: any = null;
        let notificationData: any = null;

        if (res?.ok) {
          const json = await res.json();
          createdRow = json.ticket;
          notificationData = json.notification;
        } else {
          console.warn('API /tickets create endpoint failed, attempting direct Supabase service fallback...');
          try {
            const sbTicket = await TicketService.createTicket({
              subject: d.subject.trim(),
              description: d.description ? d.description.trim() : '',
              priority: d.priority,
              impact: d.impact || undefined,
              urgency: d.urgency || undefined,
              category: d.category || 'General',
              category_id: categoryId || undefined,
              team_id: teamId || undefined,
              assignee_id: assigneeId || undefined,
              requester_id: requesterId || undefined,
              requester_name: d.requester,
              requester_email: d.requesterEmail,
              module: d.module || 'Incident',
              requestType: d.requestType || d.module,
              asset_id: d.asset_id || d.ci,
              cc: d.cc || [],
              bcc: d.bcc || [],
              custom_fields: {
                ...(d.custom || {}),
                cc: d.cc || [],
                bcc: d.bcc || [],
                ci: d.ci || undefined
              }
            });
            createdRow = sbTicket;
          } catch (sbErr: any) {
            const errData = await res?.json().catch(() => ({}));
            const errMsg = errData.message || errData.error || sbErr.message || 'Failed to create ticket on server';
            toast(`Failed to create ticket: ${errMsg}`);
            return { success: false, error: errMsg };
          }
        }

        const mapped = mapRow(createdRow);

        // Handle attachments if any
        if (d.attachments && d.attachments.length > 0 && supabase) {
          const sb = supabase;
          const targetOrgId = createdRow.organization_id || orgId;
          for (const file of d.attachments) {
            const attId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'att-' + Date.now();
            const path = `${targetOrgId}/${createdRow.id}/${attId}-${file.name}`;
            try {
              await sb.from('ticket_attachments').insert({
                ticket_id: createdRow.id,
                name: file.name,
                path,
                size: file.size,
                content_type: file.type
              });
            } catch (attErr) {
              console.error('Failed to insert attachment record:', attErr);
            }
          }
        }

        // Add confirmed server-persisted ticket to local state
        setTickets((prev) => [mapped, ...prev.filter((t) => t.id !== mapped.id)]);
        toast(`Ticket ${mapped.ref} created successfully`);

        // Synchronize all open layouts across tabs and components
        try {
          localStorage.setItem('compass_tickets_sync', Date.now().toString());
        } catch (e) {}
        window.dispatchEvent(new Event('compass_tickets_changed'));

        return { success: true, ticket: mapped, notification: notificationData };
      } catch (err: any) {
        console.error('Ticket creation error:', err);
        const errMsg = err.message || 'Unable to connect to server';
        toast(`Failed to create ticket: ${errMsg}`);
        return { success: false, error: errMsg };
      }
    },
    [orgId, toast],
  );

  const setStatus = useCallback<ConsoleStore['setStatus']>(
    (id, state) => {
      const snap = ticketsRef.current.find((t) => t.id === id);
      const label = `Status → ${state}`;
      patch(id, (t) => ({
        ...t,
        state,
        events: [...t.events, { at: Date.now(), kind: 'state', label, actor }],
      }));

      (async () => {
        const sessionRes = await supabase?.auth.getSession();
        const token = sessionRes?.data?.session?.access_token;
        if (token) {
          fetch(`${API_BASE_URL}/tickets/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ status: state })
          }).then((res) => {
            if (!res.ok) {
              console.error('Failed to update status on server');
              if (snap) restore(snap);
            } else {
              window.dispatchEvent(new Event('compass_tickets_changed'));
            }
          }).catch((err) => {
            console.error('Status update network error:', err);
            if (snap) restore(snap);
          });
        } else if (orgId && supabase) {
          supabase.from('tickets').update({ status: state }).eq('id', id).then(({ error }: { error: any }) => {
            if (error && snap) restore(snap);
          });
        }
      })();

      if (snap) toast(`${snap.ref} → ${state}`, () => restore(snap));
    },
    [actor, orgId, patch, toast, restore],
  );

  const assign = useCallback<ConsoleStore['assign']>(
    async (id, assignee, assigneeName) => {
      try {
        const updatedRow = await ticketsApi.assignTicket(id, assignee ? assignee : null);
        const mappedTicket = mapRow(updatedRow);
        setTickets((prev) => prev.map((t) => (t.id === id ? mappedTicket : t)));
        window.dispatchEvent(new Event('compass_tickets_changed'));

        const displayName = mappedTicket.assignee || assigneeName || (assignee ? 'Technician' : 'Unassigned');
        toast(assignee ? `${mappedTicket.ref} → ${displayName}` : `${mappedTicket.ref} unassigned`);
        if (assignee) {
          notify({ kind: 'assign', text: `Ticket ${mappedTicket.ref} assigned to ${displayName}`, ticketRef: mappedTicket.ref });
        }
        return { success: true, ticket: mappedTicket };
      } catch (err: any) {
        console.error('Failed to assign ticket:', err);
        const errMsg = err?.message || 'Unable to assign ticket. Please try again.';
        toast(errMsg);
        return { success: false, error: errMsg };
      }
    },
    [toast, notify],
  );

  const addUpdate = useCallback<ConsoleStore['addUpdate']>(
    (id, body, internal = false, emailData) => {
      const snap = ticketsRef.current.find((t) => t.id === id);
      patch(id, (t) => ({
        ...t,
        events: [...t.events, { at: Date.now(), kind: 'comment', label: body, actor, internal }],
      }));

      (async () => {
        const sessionRes = await supabase?.auth.getSession();
        const token = sessionRes?.data?.session?.access_token;
        if (token) {
          fetch(`${API_BASE_URL}/tickets/${id}/comments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ content: body, is_internal: internal })
          }).then((res) => {
            if (res.ok) {
              window.dispatchEvent(new Event('compass_tickets_changed'));
            }
          }).catch(console.error);
        }
      })();

      toast(emailData ? 'Email sent successfully' : internal ? 'Internal note added' : 'Reply posted');
      for (const person of mentionsIn(body)) {
        notify({ kind: 'mention', text: `${actor} mentioned ${person} on ${snap?.ref ?? ''}`.trim(), ticketRef: snap?.ref });
      }
    },
    [actor, orgId, patch, toast, notify],
  );

  const relate = useCallback<ConsoleStore['relate']>(
    (id, targetId) => {
      setTickets((prev) => {
        const targetRef = prev.find((t) => t.id === targetId)?.ref ?? targetId;
        return prev.map((t) => {
          if (t.id === id)
            return {
              ...t,
              links: [...(t.links ?? []), targetId],
              events: [...t.events, { at: Date.now(), kind: 'state', label: `Linked to ${targetRef}`, actor }],
            };
          if (t.id === targetId) return { ...t, links: [...(t.links ?? []), id] };
          return t;
        });
      });
      if (orgId && supabase) {
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label: `Linked to ticket`, actor }).then(() => {});
      }
    },
    [actor, orgId],
  );

  const merge = useCallback<ConsoleStore['merge']>(
    (id, intoId) => {
      const snap = ticketsRef.current.find((t) => t.id === id);
      const intoRef = ticketsRef.current.find((t) => t.id === intoId)?.ref ?? intoId;
      patch(id, (t) => ({
        ...t,
        mergedInto: intoId,
        state: 'Closed',
        events: [...t.events, { at: Date.now(), kind: 'state', label: `Merged into ${intoRef}`, actor }],
      }));
      if (orgId && supabase) {
        supabase.from('tickets').update({ merged_into: intoId, status: 'Closed' }).eq('id', id).then(() => {});
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label: 'Merged', actor }).then(() => {});
      }
      if (snap) toast(`${snap.ref} merged into ${intoRef}`, () => restore(snap));
    },
    [actor, orgId, patch, toast, restore],
  );

  const bulkStatus = useCallback<ConsoleStore['bulkStatus']>(
    (ids, state) => {
      const snaps = ticketsRef.current.filter((t) => ids.includes(t.id));
      setTickets((prev) =>
        prev.map((t) =>
          ids.includes(t.id)
            ? { ...t, state, events: [...t.events, { at: Date.now(), kind: 'state', label: `Status → ${state}`, actor }] }
            : t,
        ),
      );
      if (orgId && supabase) {
        ids.forEach((id) => supabase!.from('tickets').update({ status: state }).eq('id', id).then(() => {}));
      }
      toast(`${ids.length} ticket${ids.length > 1 ? 's' : ''} → ${state}`, () => snaps.forEach(restore));
    },
    [actor, orgId, toast, restore],
  );

  const updateField = useCallback<ConsoleStore['updateField']>(
    (id, field, value) => {
      const snap = ticketsRef.current.find((t) => t.id === id);
      patch(id, (t) => ({
        ...t,
        [field]: value,
        events: [...t.events, { at: Date.now(), kind: 'state', label: `Updated ${field}`, actor }],
      }));
      if (orgId && supabase) {
        // Map frontend fields to db columns
        const colMap: Record<string, string> = {
          category: 'category_id', 
          impact: 'impact',
          urgency: 'urgency',
          dueAt: 'due_at',
        };
        const col = colMap[field] || field;
        
        let dbValue = value;
        if (field === 'dueAt' && value) {
          dbValue = new Date(value).toISOString();
        }

        supabase.from('tickets').update({ [col]: dbValue }).eq('id', id).then(() => {});
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label: `Updated ${field}`, actor }).then(() => {});
      }
      if (snap) toast(`Updated ${field}`, () => restore(snap));
    },
    [actor, orgId, patch, toast, restore],
  );

  const bulkAssign = useCallback<ConsoleStore['bulkAssign']>(
    async (ids, assignee, assigneeName) => {
      const displayName = assigneeName || assignee;
      try {
        const results = await Promise.all(
          ids.map((id) => ticketsApi.assignTicket(id, assignee || null))
        );
        const mappedMap = new Map(results.map((r) => [r.id, mapRow(r)]));
        setTickets((prev) => prev.map((t) => mappedMap.get(t.id) || t));
        window.dispatchEvent(new Event('compass_tickets_changed'));
        toast(`${ids.length} ticket${ids.length > 1 ? 's' : ''} ${assignee ? `→ ${displayName}` : 'unassigned'}`);
      } catch (err: any) {
        console.error('Failed to bulk assign tickets:', err);
        toast(`Failed to update some tickets: ${err?.message || 'Error'}`);
      }
    },
    [toast],
  );

  const bulkUpdate = useCallback<ConsoleStore['bulkUpdate']>(
    (ids, body) => {
      setTickets((prev) =>
        prev.map((t) =>
          ids.includes(t.id)
            ? { ...t, events: [...t.events, { at: Date.now(), kind: 'comment', label: body, actor }] }
            : t,
        ),
      );
      if (orgId && supabase) {
        const authorId = userIdRef.current || orgId;
        ids.forEach((id) => supabase!.from('ticket_comments').insert({ ticket_id: id, content: body, is_internal: false, author_id: authorId }).then(() => {}));
      }
      toast(`Replied to ${ids.length} ticket${ids.length > 1 ? 's' : ''}`);
    },
    [actor, orgId, toast],
  );

  const addAttachment = useCallback<ConsoleStore['addAttachment']>(
    (ticketId, file) => {
      const id = typeof crypto !== 'undefined' && crypto.randomUUID 
        ? crypto.randomUUID() 
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) { 
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8); 
            return v.toString(16); 
          });
      const url = URL.createObjectURL(file);
      patch(ticketId, (t) => ({
        ...t,
        attachments: [...(t.attachments ?? []), { id, name: file.name, size: file.size, type: file.type, url }],
      }));
      if (orgId && supabase) {
        const sb = supabase;
        const path = `${orgId}/${ticketId}/${id}-${file.name}`;
        sb.storage
          .from('attachments')
          .upload(path, file)
          .then(({ error }: any) => {
            if (!error)
              sb.from('ticket_attachments')
                .insert({ ticket_id: ticketId, name: file.name, path, size: file.size, content_type: file.type })
                .then(() => {});
          });
      }
      toast(`Attached ${file.name}`);
    },
    [orgId, patch, toast],
  );

  const removeAttachment = useCallback<ConsoleStore['removeAttachment']>(
    (ticketId, attId) => {
      patch(ticketId, (t) => ({ ...t, attachments: (t.attachments ?? []).filter((a) => a.id !== attId) }));
    },
    [patch],
  );

  const resolveTicket = useCallback<ConsoleStore['resolveTicket']>(
    (id, state, code, notes) => {
      const snap = ticketsRef.current.find((t) => t.id === id);
      const label = `${state} — ${code}${notes ? `: ${notes}` : ''}`;
      patch(id, (t) => ({
        ...t,
        state,
        resolutionCode: code,
        closureNotes: notes,
        events: [...t.events, { at: Date.now(), kind: 'state', label, actor }],
      }));
      if (orgId && supabase) {
        supabase.from('tickets').update({ status: state, resolution_code: code, closure_notes: notes }).eq('id', id).then(({ error }: { error: any }) => {
          if (error) {
            console.error('Failed to resolve/close ticket:', error);
            toast('Failed to update ticket status in database.');
            if (snap) restore(snap);
          } else if (snap) {
            notifyTicketEvent({ ...snap, state }, 'status_changed');
          }
        });
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label, actor }).then(() => {});
      }
      if (snap) toast(`${snap.ref} ${state.toLowerCase()} (${code})`, () => restore(snap));
    },
    [actor, orgId, patch, toast, restore],
  );

  const setParent = useCallback<ConsoleStore['setParent']>(
    (id, parentId) => {
      const snap = ticketsRef.current.find((t) => t.id === id);
      const parentRef = parentId ? ticketsRef.current.find((t) => t.id === parentId)?.ref ?? parentId : null;
      const label = parentId ? `Set as sub-ticket of ${parentRef}` : 'Removed parent';
      patch(id, (t) => ({ ...t, parentId, events: [...t.events, { at: Date.now(), kind: 'state', label, actor }] }));
      if (orgId && supabase) {
        supabase.from('tickets').update({ parent_id: parentId }).eq('id', id).then(() => {});
      }
      if (snap) toast(label, () => restore(snap));
    },
    [actor, orgId, patch, toast, restore],
  );

  const escalate = useCallback<ConsoleStore['escalate']>(
    (id, assignee, level, label) => {
      const ref = ticketsRef.current.find((t) => t.id === id)?.ref;
      patch(id, (t) => ({
        ...t,
        assignee,
        escalationLevel: level,
        events: [...t.events, { at: Date.now(), kind: 'state', label, actor: 'escalation' }],
      }));
      if (orgId && supabase) {
        supabase.from('tickets').update({ assignee_id: assignee, escalation_level: level }).eq('id', id).then(() => {});
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label, actor: 'escalation' }).then(() => {});
      }
      notify({ kind: 'breach', text: label, ticketRef: ref });
    },
    [orgId, patch, notify],
  );

  const requestApproval = useCallback<ConsoleStore['requestApproval']>(
    (id, approver) => {
      const aId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `ap-${Date.now()}`;
      const ref = ticketsRef.current.find((t) => t.id === id)?.ref;
      let next: ConsoleStore['tickets'][number]['approvals'] = [];
      patch(id, (t) => {
        next = [...(t.approvals ?? []), { id: aId, approver, status: 'pending' as const }];
        return { ...t, approvals: next, events: [...t.events, { at: Date.now(), kind: 'state', label: `Approval requested from ${approver}`, actor }] };
      });
      if (orgId && supabase) {
        supabase.from('tickets').update({ approvals: next }).eq('id', id).then(() => {});
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label: `Approval requested from ${approver}`, actor }).then(() => {});
      }
      notify({ kind: 'mention', text: `${approver}: approval requested on ${ref ?? ''}`.trim(), ticketRef: ref });
    },
    [actor, orgId, patch, notify],
  );

  const decideApproval = useCallback<ConsoleStore['decideApproval']>(
    (id, approvalId, decision, note) => {
      const ref = ticketsRef.current.find((t) => t.id === id)?.ref;
      let next: ConsoleStore['tickets'][number]['approvals'] = [];
      let who = '';
      patch(id, (t) => {
        next = (t.approvals ?? []).map((a) => {
          if (a.id !== approvalId) return a;
          who = a.approver;
          return { ...a, status: decision, note, at: Date.now() };
        });
        const allApproved = next.length > 0 && next.every((a) => a.status === 'approved');
        const label = `${who} ${decision} the request${note ? `: ${note}` : ''}`;
        const evts = [...t.events, { at: Date.now(), kind: 'state' as const, label, actor }];
        if (allApproved) evts.push({ at: Date.now() + 1, kind: 'state' as const, label: 'All approvals complete', actor: 'workflow' });
        return { ...t, approvals: next, events: evts };
      });
      if (orgId && supabase) {
        supabase.from('tickets').update({ approvals: next }).eq('id', id).then(() => {});
        supabase.from('ticket_events').insert({ organization_id: orgId, ticket_id: id, kind: 'state', label: `${who} ${decision} the request`, actor }).then(() => {});
      }
      toast(`Request ${decision}`, undefined);
      notify({ kind: 'comment', text: `${ref ?? 'Request'} ${decision} by ${who}`, ticketRef: ref });
    },
    [actor, orgId, patch, toast, notify],
  );

  return { 
    tickets, 
    source, 
    isLoading,
    error: loadError,
    createTicket, 
    setStatus, 
    assign, 
    addUpdate, 
    updateField, 
    relate, 
    merge, 
    bulkAssign, 
    bulkStatus, 
    bulkUpdate, 
    addAttachment, 
    removeAttachment, 
    resolveTicket, 
    setParent, 
    escalate, 
    requestApproval, 
    decideApproval, 
    refreshTickets: loadTickets 
  };
}
